import type { OpenDrawer } from '@app/shared/types/drawer'
import { isDOM } from '@app/shared/predicates'

import debounce from 'lodash/debounce'

type DrawerPosition = Exclude<OpenDrawer, 'none'>

const collapseDrawer = (
  drawer: HTMLElement,
  modifier: DrawerPosition
): void => {
  window.requestAnimationFrame(() => {
    drawer.classList.remove(`sf-sidebar__expanded--${modifier}`)
    drawer.classList.add(`sf-sidebar__collapsed--${modifier}`)
  })
}

const expandDrawer = (drawer: HTMLElement, modifier: DrawerPosition): void => {
  window.requestAnimationFrame(() => {
    drawer.classList.remove(`sf-sidebar__collapsed--${modifier}`)
    drawer.classList.add(`sf-sidebar__expanded--${modifier}`)
  })
}

const toggleMainExpandClass = (
  mainContent: HTMLElement,
  shouldExpand: boolean
): void => {
  if (shouldExpand) {
    window.requestAnimationFrame(() => {
      mainContent?.classList.add('two-drawer-layout__main--sidebar-expanded')
    })
  } else {
    window.requestAnimationFrame(() => {
      mainContent?.classList.remove('two-drawer-layout__main--sidebar-expanded')
    })
  }
}

export default (): Array<() => void> => {
  const unsubscribers: Array<() => void> = []

  window.onDomLoad(($dom) => {
    const dom = $dom[0]

    if (!isDOM(dom)) {
      return
    }

    const leftDrawer = dom.getElementById('left-drawer')
    const rightDrawer = dom.getElementById('right-drawer')
    const mainContent = dom.getElementById('main-content')

    const hasAnyDrawer = leftDrawer || rightDrawer

    if (!hasAnyDrawer) {
      return
    }

    unsubscribers.forEach((unsubscribe) => unsubscribe())

    // Make the animation smoother when the drawer expands, only adding the max-width class after the animation has stopped
    const makeHandleMainContentClass =
      (drawer: HTMLElement, position: DrawerPosition) => (): void => {
        if (!mainContent) {
          return
        }

        if (!isDrawerCollapsed(drawer, position)) {
          toggleMainExpandClass(mainContent, true)
        }
      }

    const addAnimationListeners = (
      drawer: HTMLElement,
      position: DrawerPosition
    ): void => {
      const emitAnimationEndEvent = debounce(
        makeEmitAnimationEndEvent(drawer),
        300,
        {
          leading: true,
          trailing: false,
        }
      )

      const emitAnimationStartEvent = debounce(
        makeEmitAnimationStartEvent(drawer),
        300,
        {
          leading: true,
          trailing: false,
        }
      )

      const handleMainContentClass = makeHandleMainContentClass(
        drawer,
        position
      )

      drawer.addEventListener('transitionstart', emitAnimationStartEvent)
      drawer.addEventListener('transitionend', emitAnimationEndEvent)
      drawer.addEventListener('transitionend', handleMainContentClass)

      unsubscribers.push(() => {
        drawer.removeEventListener('transitionstart', emitAnimationStartEvent)
        drawer.removeEventListener('transitionend', emitAnimationEndEvent)
        drawer.removeEventListener('transitionend', handleMainContentClass)
      })
    }

    const makeEmitAnimationEndEvent =
      (drawer: HTMLElement) =>
      (e: Event): void => {
        if (e.target === drawer) {
          window.eventHub.$emit('drawer:animation-end')
        }
      }

    const makeEmitAnimationStartEvent =
      (drawer: HTMLElement) =>
      (e: Event): void => {
        if (e.target === drawer) {
          window.eventHub.$emit('drawer:animation-start')
        }
      }

    if (leftDrawer) {
      addAnimationListeners(leftDrawer, 'left')
    }

    if (rightDrawer) {
      addAnimationListeners(rightDrawer, 'right')
    }

    const isDrawerCollapsed = (
      drawer: HTMLElement | null,
      position: DrawerPosition
    ): boolean => {
      if (!drawer) {
        return false
      }

      return drawer.classList.contains(`sf-sidebar__collapsed--${position}`)
    }

    const closeDrawer = (position: DrawerPosition): void => {
      const drawer = position === 'left' ? leftDrawer : rightDrawer

      if (!drawer || isDrawerCollapsed(drawer, position)) {
        return
      }

      collapseDrawer(drawer, position)
    }

    const openDrawer = (position: DrawerPosition): void => {
      const drawer = position === 'left' ? leftDrawer : rightDrawer

      if (!drawer || !isDrawerCollapsed(drawer, position)) {
        return
      }

      expandDrawer(drawer, position)
    }

    const handleDrawerOpen = (drawer: DrawerPosition): void => {
      if (drawer !== 'left' && drawer !== 'right') {
        return
      }

      const oppositeDrawer = drawer === 'left' ? 'right' : 'left'

      openDrawer(drawer)
      closeDrawer(oppositeDrawer)
    }

    const handleDrawerClose = (): void => {
      closeDrawer('left')
      closeDrawer('right')

      if (mainContent) {
        toggleMainExpandClass(mainContent, false)
      }
    }

    window.eventHub.$on('drawer:open', handleDrawerOpen)
    window.eventHub.$on('drawer:close', handleDrawerClose)

    unsubscribers.push(() => {
      window.eventHub.$off('drawer:open', handleDrawerOpen)
      window.eventHub.$off('drawer:close', handleDrawerClose)
    })
  })

  return unsubscribers
}
